import { type FocusEvent, type ReactNode } from 'react';
import { Button, Stack } from '../../atoms';
import { InlineNotice, StatusBlock } from '../../molecules';
import {
	DetailField as Field,
	DetailSection as Section,
	DetailTextInput as TextInput,
	DetailToggle as Toggle,
} from '../../molecules/detail-fields/detail-fields';
import { CerAreaView } from './cer-area-view';

export type CerDetailsForm = Record<string, string | boolean>;
export interface CerDetailsViewProps {
	nome: string;
	branding: ReactNode;
	features?: ReactNode;
	/** Amministratori della CER — solo per il superadmin. */
	admins?: ReactNode;
	form: CerDetailsForm;
	loading: boolean;
	loadError: boolean;
	saving: boolean;
	error: string;
	/** Toglie l'errore dell'ultimo salvataggio: si torna su un campo per correggerlo. */
	onDismissError?: () => void;
	canDisable: boolean;
	onFieldChange: (key: string, value: string | boolean) => void;
	onSave: () => void;
	onCancel: () => void;
	/** Vero se la CER è cambiata altrove mentre ci sono modifiche non salvate qui. */
	remoteChange?: boolean;
	/** Ricarica i dati dal server, scartando le modifiche non salvate. */
	onReloadRemote?: () => void;
}

export function CerDetailsView({
	nome,
	branding,
	features,
	admins,
	form,
	loading,
	loadError,
	saving,
	error,
	onDismissError,
	canDisable,
	onFieldChange: set,
	onSave,
	onCancel,
	remoteChange = false,
	onReloadRemote,
}: CerDetailsViewProps) {
	const s = (key: string) => (form[key] as string) ?? '';
	const b = (key: string) => !!form[key];
	if (loading) return <StatusBlock kind="loading" size="lg" message="Caricamento..." />;
	if (loadError)
		return (
			<StatusBlock
				kind="error"
				size="lg"
				message="Impossibile caricare i dati della CER. Riprova ricaricando la pagina."
			/>
		);

	// Basta tornare su un campo per correggerlo: l'errore in alto non deve
	// restare finché non si scrive (N-01).
	const dismissOnFieldFocus = (e: FocusEvent<HTMLDivElement>) => {
		if (error && /^(INPUT|SELECT|TEXTAREA)$/.test(e.target.tagName)) onDismissError?.();
	};

	const fiscal = (
		<div onFocus={dismissOnFieldFocus}>
			<Stack gap="lg">
				{canDisable && (
					<Section title="Stato">
						<Toggle
							label="CER disabilitata"
							hint="Nascosta a soci e referenti: la CER e tutte le sue configurazioni spariscono da switcher e viste dati finché non viene riabilitata"
							checked={b('disabled')}
							onChange={() => set('disabled', !b('disabled'))}
						/>
					</Section>
				)}

				{remoteChange && (
					<InlineNotice tone="warn">
						<span className="flex flex-wrap items-center justify-between gap-2">
							<span>
								Questa CER è stata modificata altrove. Le tue modifiche non salvate restano qui:
								salva per sovrascrivere, oppure ricarica per vedere i dati aggiornati.
							</span>
							{onReloadRemote && (
								<Button type="button" variant="outline" size="sm" onClick={onReloadRemote}>
									Ricarica e scarta le mie modifiche
								</Button>
							)}
						</span>
					</InlineNotice>
				)}

				{error && <InlineNotice tone="danger">{error}</InlineNotice>}

				<Section title="Denominazione & Territorio">
					<Field label="Nome / Denominazione">
						<TextInput value={s('nome')} onChange={(v) => set('nome', v)} />
					</Field>
					<Field label="Comune sede">
						<TextInput value={s('comune')} onChange={(v) => set('comune', v)} />
					</Field>
				</Section>
			</Stack>
		</div>
	);
	return (
		<CerAreaView
			nome={nome}
			onCancel={onCancel}
			onSave={onSave}
			saving={saving}
			admins={admins}
			fiscal={fiscal}
			branding={branding}
			features={features}
		/>
	);
}
